import { useState } from 'react';
import { useReadingWaveform } from '@/hooks/useReadingWaveform';
import { waveformPath } from '@/utils/readingWaveform';
import { useAuth } from '@/hooks/useAuth';
import { Radio, ChevronDown } from 'lucide-react';

const WIDTH = 220;
const HEIGHT = 44;

/**
 * Quiet, ambient floating overlay on Discovery that surfaces the user's
 * reading-frequency waveform and their resonance with people they follow.
 *
 * Mounts only for authenticated users. Anonymous visitors see nothing.
 */
export function ReadingVibeOverlay() {
  const { user, loading: authLoading } = useAuth();
  const { loading, wave, totalSignals, matches, cohortResonance } = useReadingWaveform();
  const [expanded, setExpanded] = useState(false);

  if (authLoading || !user) return null;
  if (loading && totalSignals === 0) return null;
  if (totalSignals === 0) return null;

  const d = waveformPath(wave, WIDTH, HEIGHT);
  const resonancePct = Math.round(cohortResonance * 100);

  return (
    <aside
      aria-label="Reading vibe waveform"
      className="fixed bottom-4 left-4 z-40 pointer-events-auto select-none"
    >
      <div className="rounded-2xl border border-slate-700/60 bg-slate-900/60 backdrop-blur-xl shadow-2xl shadow-slate-950/40 overflow-hidden">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-3 px-3 py-2 w-full focus-ring rounded-2xl"
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse reading vibe' : 'Expand reading vibe'}
        >
          <Radio className="w-3.5 h-3.5 text-cyan-400/80 shrink-0" aria-hidden />
          <svg
            width={WIDTH}
            height={HEIGHT}
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            className="shrink-0"
            aria-hidden
          >
            <defs>
              <linearGradient id="vibe-line" x1="0" x2="1" y1="0" y2="0">
                <stop offset="0%" stopColor="hsl(189 51% 56% / 0.15)" />
                <stop offset="40%" stopColor="hsl(189 51% 56% / 0.85)" />
                <stop offset="100%" stopColor="hsl(187 73% 67% / 1)" />
              </linearGradient>
            </defs>
            <path
              d={d}
              fill="none"
              stroke="url(#vibe-line)"
              strokeWidth={1.25}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {matches.length > 0 && (
            <span className="flex items-center gap-1.5 text-[11px] tracking-wide text-slate-300 shrink-0">
              <span className="font-mono text-cyan-300">{resonancePct}%</span>
              <ChevronDown
                className={`w-3 h-3 text-slate-500 transition-transform ${
                  expanded ? 'rotate-180' : ''
                }`}
                aria-hidden
              />
            </span>
          )}
        </button>

        {expanded && (
          <div className="px-3 pb-3 pt-1 border-t border-slate-800/60 max-w-[320px]">
            <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-2">
              {matches.length === 0
                ? 'Tune in — follow other readers to resonate'
                : 'Resonance with people you follow'}
            </p>
            <ul className="space-y-1.5">
              {matches.map((m) => (
                <li
                  key={m.userId}
                  className="flex items-center gap-2 text-xs text-slate-300"
                >
                  {m.avatarUrl ? (
                    <img
                      src={m.avatarUrl}
                      alt=""
                      className="w-5 h-5 rounded-full object-cover border border-slate-700/60"
                      loading="lazy"
                    />
                  ) : (
                    <span className="w-5 h-5 rounded-full bg-slate-800 border border-slate-700/60" />
                  )}
                  <span className="flex-1 truncate">
                    {m.displayName || 'Unnamed reader'}
                  </span>
                  <span className="font-mono text-[11px] text-cyan-300/90">
                    {Math.round(m.score * 100)}%
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-[10px] text-slate-500 mt-2 leading-relaxed">
              Waveform = frequency × status × rating, last 90 days.
            </p>
          </div>
        )}
      </div>
    </aside>
  );
}
